import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FormAutofillPhase,
  FormValueSuggestionSource,
  type FormSuggestionSource,
} from "@api/shared-runtime/enums";
import { useTRPC, type RouterOutputs } from "@/trpc";

type AutofillResult = RouterOutputs["organizationForm"]["getFormAutofill"];
export type FieldSuggestion = AutofillResult["suggestions"][number];

export interface UseFormAutofillInput {
  formId: string;
  sources: FormSuggestionSource[];
  /** Off for PDF forms and when the caller hasn't opted in. */
  enabled: boolean;
}

export interface UseFormAutofillResult {
  suggestions: FieldSuggestion[] | undefined;
  /**
   * Vetoed fields — already absent from `suggestions`; the caller clears any
   * fast-pass paint the user hasn't touched.
   */
  vetoedFieldIds: string[];
  /** The AI pass is still running; the deterministic values are already shown. */
  isThinking: boolean;
  /** True once both passes have settled, for one-shot analytics. */
  hasSettled: boolean;
  /** How many suggestions the deterministic pass produced. */
  deterministicCount: number;
  /** How many additional answers the AI pass produced. */
  aiAnswerCount: number;
  reset: () => void;
}

/**
 * Two-pass autofill: deterministic sources paint immediately, then a second
 * request adds AI answers and vetoes contradicted paints. The dialog sees one
 * growing suggestion list.
 */
export function useFormAutofill({
  formId,
  sources,
  enabled,
}: UseFormAutofillInput): UseFormAutofillResult {
  const trpc = useTRPC();

  const fastQuery = useQuery(
    trpc.organizationForm.getFormAutofill.queryOptions(
      { formId, sources, phase: FormAutofillPhase.Fast },
      { enabled },
    ),
  );

  // `isSuccess` alone is not enough: a cached result is successful the
  // instant the query mounts, even while refetching — the AI pass would run
  // against data about to be replaced.
  const isFastSettled = fastQuery.isSuccess && !fastQuery.isFetching;

  const completeQuery = useQuery(
    trpc.organizationForm.getFormAutofill.queryOptions(
      { formId, sources, phase: FormAutofillPhase.Complete },
      { enabled: enabled && isFastSettled },
    ),
  );

  // A disabled query keeps cached data with `isFetching: false`; without the
  // gate a reopened dialog seeds last session's answers, and `seededKeysRef`
  // then blocks the fresh ones.
  const isCompleteUsable =
    enabled &&
    isFastSettled &&
    completeQuery.isSuccess &&
    !completeQuery.isFetching;

  // The server only produces vetoes when it actually calls the model, so a
  // cache-hit response carries none — retain the ones already earned or a
  // background refetch resurrects a vetoed value.
  const [vetoedFieldIds, setVetoedFieldIds] = useState<string[]>([]);

  const freshVetoes = isCompleteUsable
    ? completeQuery.data?.vetoedFieldIds
    : undefined;
  useEffect(() => {
    if (!freshVetoes || freshVetoes.length === 0) {
      return;
    }
    setVetoedFieldIds((prev) => {
      const merged = new Set([...prev, ...freshVetoes]);
      return merged.size === prev.length ? prev : [...merged];
    });
  }, [freshVetoes]);

  const fastSuggestions = fastQuery.data?.suggestions;
  const completeSuggestions = isCompleteUsable
    ? completeQuery.data?.suggestions
    : undefined;

  // The complete result is a superset of the fast one (minus vetoes); keep
  // the fast result until it arrives, and if the AI pass fails outright.
  const suggestions = useMemo(() => {
    // A closed dialog must not be handed values to seed.
    if (!enabled) {
      return undefined;
    }
    if (completeSuggestions) {
      return completeSuggestions;
    }
    return fastSuggestions;
  }, [enabled, completeSuggestions, fastSuggestions]);

  const deterministicCount = fastSuggestions?.length ?? 0;
  // The two passes can see different deterministic data, so subtracting one
  // response length from the other would misreport as AI answers.
  const aiAnswerCount = (completeSuggestions ?? []).filter(
    (suggestion) => suggestion.source === FormValueSuggestionSource.AiGenerated,
  ).length;

  // Fetching, not just pending: dropping the indicator mid-refetch makes
  // late-arriving fields look like they filled themselves.
  const isCompleteInFlight =
    isFastSettled && (completeQuery.isPending || completeQuery.isFetching);
  // Without this a failed fast pass leaves `hasSettled` false forever and the
  // one-shot analytics event never fires.
  const isFastDone = isFastSettled || fastQuery.isError;

  return {
    suggestions,
    vetoedFieldIds,
    isThinking: enabled && isFastSettled && isCompleteInFlight,
    hasSettled: !enabled || (isFastDone && !isCompleteInFlight),
    deterministicCount,
    aiAnswerCount,
    reset: () => setVetoedFieldIds([]),
  };
}
